import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { captureSupabaseError } from '../lib/sentry';

interface DeleteResult {
  success: boolean;
  message?: string;
}

export function useDeleteDocument() {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DeleteResult | null>(null);

  const deleteDocument = useCallback(async (documentId: string) => {
    setDeleting(true);
    setError(null);
    setResult(null);

    try {
      const { data, error } = await supabase.functions.invoke(
        'delete-document',
        {
          body: { documentId },
        }
      );

      if (error) {
        setError(error.message);
        setResult({ success: false, message: error.message });
      } else {
        // Edge function already returns JSON like { success: true }
        const parsed = typeof data === 'string' ? JSON.parse(data) : data;
        setResult(parsed);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      captureSupabaseError(err, { op: 'documents.delete' });
      setError(err.message ?? 'Unknown error');
      setResult({ success: false, message: err.message });
    } finally {
      setDeleting(false);
    }
  }, []);

  return { deleteDocument, deleting, error, result };
}
